import { Link, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import moment from 'moment';
import React, { memo, useContext, useMemo, useState } from 'react';
import { FileStructureContext } from '../../../../../contexts/FileStructureContext';
import theme from '../../../../../theme';
import { CompanyFile, Dashboard, EmailBody } from '../../../../../types/files';
import ArrayUtils from '../../../../../utils/ArrayUtils';
import DocumentsModal, { Attachment, detectGoogleLinkType, detectOfficeLinkType, detectDocSendLinkType } from '../../../../modals/tables/dashboards/rows/DocumentsModal';
import useStyles from "../styles";

export const useLocalStyles = makeStyles((theme) => ({
    count: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    noCount: {
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    received: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 400,
        textDecoration: 'none',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    pdfWrapper: {
        padding: '2px 16px',
        backgroundColor: theme.colors.error['50'],
        border: `1px solid ${theme.colors.error['50']}`,
        borderRadius: 8,
        cursor: 'pointer',
    },
    deckLinkWrapper: {
        padding: '4px 16px',
        backgroundColor: theme.colors.blue['50'],
        border: `1px solid #eff6ff`,
        borderRadius: 8,
    },
}));

const DocumentsCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const localClasses = useLocalStyles();
    const { fileStructure } = useContext(FileStructureContext);
    const [documentsModalOpen, setDocumentsModalOpen] = useState<boolean>(false);

    const files = useMemo(() => fileStructure
        .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
        .sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboard, fileStructure]);

    const dashboardEmailsWithDocs = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];
        const emailActivities: EmailBody[] = ArrayUtils.sortByAscending(activityItems ?? [], 'createdAt');
        const unmatchedFiles: CompanyFile[] = [];
        const fileMatchedMap = new Map<string, boolean>();
        const unmatchedLinks: string[] = [];
        const linkMatchedMap = new Map<string, boolean>();

        const emailsWithDocs: Array<EmailBody & { attached: CompanyFile[], linked: string[] }> = emailActivities.map(emailBody => ({...emailBody,
            attached: (() => {
                const matched = new Map<string, CompanyFile>();
                const attachments = emailBody.attachments?.map(fileName => fileName.trim().toLowerCase()) || [];

                files.forEach(file => {
                    const timeDiff = moment(file.createdAt).diff(moment(emailBody.createdAt), 'minutes', true);
                    if (attachments.includes(file.name.trim().toLowerCase()) || (timeDiff >= 0 && timeDiff <= 5)) {
                        if (!matched.has(file.id)) {
                            matched.set(file.id, file);
                            fileMatchedMap.set(file.id, true);
                        }
                    }
                });

                return Array.from(matched.values());
            })(),
            linked: (() => {
                const seen = new Set<string>();
                const matchedLinks: string[] = [];
                const linksFromEmail = emailBody.externalLinks?.map(extLink => extLink.trim()) || [];

                (dashboard?.externalLinks || []).forEach(link => {
                    const t = link.trim();
                    if ((linksFromEmail.includes(t) || (emailBody?.content || '').includes(t)) && !seen.has(t)) {
                        seen.add(t);
                        matchedLinks.push(t);
                        linkMatchedMap.set(t, true);
                    }
                });

                return matchedLinks;
            })(),
        }));

        files.forEach(file => !fileMatchedMap.has(file.id) ? unmatchedFiles.push(file) : {});
        dashboard?.externalLinks?.forEach(link => !linkMatchedMap.has(link.trim()) ? unmatchedLinks.push(link) : {});
        if (emailsWithDocs.length > 0) {
            if (unmatchedFiles.length > 0)
                emailsWithDocs[0].attached = [ ...emailsWithDocs[0].attached, ...unmatchedFiles];
            if (unmatchedLinks.length > 0)
                emailsWithDocs[0].linked = [ ...emailsWithDocs[0].linked, ...unmatchedLinks];
        } else if (unmatchedFiles.length > 0 || unmatchedLinks.length > 0) {
            emailsWithDocs.push({
                id: `${dashboard.id}-unmatched`,
                createdAt: unmatchedFiles[0]?.createdAt || dashboard.createdAt,
                type: 'EMAIL',
                attached: unmatchedFiles,
                linked: unmatchedLinks,
            } as EmailBody & { attached: CompanyFile[], linked: string[] });
        }

        return emailsWithDocs.filter(emailWithDocs => emailWithDocs.attached.length > 0 || emailWithDocs.linked.length > 0)
            .map(emailWithDocs => ({
                ...emailWithDocs,
                attached: emailWithDocs.attached.map(file => ({...file,
                    id: `${emailWithDocs.id}-file-${file.id}`,
                    isLink: false,
                    filePath: file.path,
                    fileType: file.name.toLowerCase().split('.').pop() || '',
                })) as Attachment[],
                linked: Array.from(new Set(emailWithDocs.linked)).map((link, i) => {
                    const googleType = detectGoogleLinkType(link);
                    const officeType = detectOfficeLinkType(link);
                    const docSendType = detectDocSendLinkType(link);
                    const detectedLinkType = googleType || officeType || docSendType;

                    return {
                        id: `${emailWithDocs.id}-link-${i}`,
                        name: dashboard.title + ' - External Link',
                        createdAt: new Date(dashboard.createdAt),
                        isLink: true,
                        linkUrl: link,
                        linkType: detectedLinkType,
                    };
                }) as Attachment[],
            }));
    }, [dashboard, files]);

    const attachments = useMemo(() =>
        dashboardEmailsWithDocs.reduce((sum, email) =>
            sum + email.attached.length + email.linked.length
        , 0)
    , [dashboardEmailsWithDocs]);

    return (<>
        <Stack width="100%">
            {(attachments > 0) ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    <Stack direction="column" alignItems="flex-start" justifyContent="center">
                        <Link onClick={() => setDocumentsModalOpen(true)} underline="hover" target="_self"
                            sx={{ cursor: 'pointer', color: theme.colors.neutral['700'] }}>
                            <Typography className={classNames(localClasses.count, !attachments && localClasses.noCount)}
                                sx={!attachments ? { fontWeight: `500 !important` } : {}}>
                                {`${attachments}`}
                                <Typography sx={{ fontFamily: 'Inter', fontSize: '0.9rem !important', fontWeight: '400'}} display="inline">
                                    {!attachments ? '' : ` document${attachments > 1 ? 's' : ''} received`}
                                </Typography>
                            </Typography>
                        </Link>
                        <Typography className={localClasses.received}>
                            {`Last: ${!!files?.length ? moment(files[0].createdAt).fromNow() : moment(dashboard.createdAt).fromNow()}`}
                        </Typography>
                    </Stack>
                </Stack>
            ): (<Typography className={classes.url} fontSize="0.9rem !important" color={`${theme.colors.neutral['500']} !important`}>
                    {'No documents'}
                </Typography>
            )}
        </Stack>
        {documentsModalOpen && (
            <DocumentsModal
                open={documentsModalOpen}
                dashboard={dashboard}
                dashboardEmailsWithDocs={dashboardEmailsWithDocs}
                onClose={() => setDocumentsModalOpen(false) } />
        )}
    </>);
}

export default memo(DocumentsCell);
